import { useMemo, useState } from "react";
import { animeList } from "../../../mocks/anime";

interface RecommendedSectionProps {
  searchQuery: string;
  activeTag: string;
  onSearch: (keyword: string) => void;
  onTagChange: (tag: string) => void;
}

const tagOptions = ["全部", "后宫", "逆后宫", "暧昧", "攻略", "高甜", "恋爱"];

export default function RecommendedSection({
  searchQuery,
  activeTag,
  onSearch,
  onTagChange,
}: RecommendedSectionProps) {
  const [input, setInput] = useState(searchQuery);

  const filtered = useMemo(() => {
    let list = animeList;
    if (activeTag !== "全部") {
      list = list.filter((item) => item.tags.includes(activeTag));
    }
    if (searchQuery) {
      const kw = searchQuery.toLowerCase();
      list = list.filter(
        (item) =>
          item.title.toLowerCase().includes(kw) ||
          item.desc.toLowerCase().includes(kw) ||
          item.tags.some((t) => t.toLowerCase().includes(kw)),
      );
    }
    return list;
  }, [activeTag, searchQuery]);

  const handleSubmit = () => {
    onSearch(input.trim());
  };

  return (
    <section id="recommend" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-[1400px] px-4 md:px-6">
        <div className="mb-6 flex flex-col items-start justify-between gap-4 md:mb-8 md:flex-row md:items-end">
          <div>
            <p className="mb-2 text-xs font-bold tracking-widest text-primary-600">热门推荐</p>
            <h2 className="font-display text-2xl text-foreground-950 md:text-4xl">
              合口味的女神动漫，一搜即达
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              结合关键词与标签，帮你在海量作品里快速定位心仪的恋爱番，追更不迷路。
            </p>
          </div>

          <div className="flex w-full max-w-sm items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
            <i className="ri-search-line text-lg text-foreground-400"></i>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              placeholder="搜索作品或题材…"
              className="h-11 w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button
              onClick={handleSubmit}
              className="whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-secondary-600"
            >
              筛选
            </button>
          </div>
        </div>

        <div className="mb-8 flex flex-wrap items-center gap-2">
          {tagOptions.map((tag) => (
            <button
              key={tag}
              onClick={() => onTagChange(tag)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${
                activeTag === tag
                  ? "bg-primary-500 text-white"
                  : "border border-background-300 bg-background-100 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              {tag}
            </button>
          ))}
        </div>

        {searchQuery && (
          <p className="mb-6 text-sm text-foreground-600">
            搜索「<span className="font-bold text-primary-600">{searchQuery}</span>」共找到{" "}
            <span className="font-bold">{filtered.length}</span> 部作品
          </p>
        )}

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg bg-background-100 py-16 text-center">
            <i className="ri-inbox-line text-4xl text-foreground-300"></i>
            <p className="mt-4 text-sm text-foreground-600">没有找到匹配的作品，换个关键词试试吧</p>
            <button
              onClick={() => {
                onSearch("");
                onTagChange("全部");
                setInput("");
              }}
              className="mt-4 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-primary-600"
            >
              查看全部推荐
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((item) => (
              <article
                key={item.id}
                className="group overflow-hidden rounded-lg bg-background-100 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={item.image}
                    alt={`${item.title} 女神动漫后宫恋爱番推荐海报`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-xs font-bold text-amber-300 backdrop-blur">
                    <i className="ri-star-fill"></i>
                    {item.score}
                  </span>
                  <span className="absolute right-3 top-3 rounded-full bg-accent-500 px-2.5 py-1 text-[11px] font-bold text-white">
                    {item.status}
                  </span>
                </div>
                <div className="p-4">
                  <h3 className="font-display text-base text-foreground-950">{item.title}</h3>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {item.tags.map((t) => (
                      <span
                        key={t}
                        className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-[11px] font-medium text-secondary-900"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-foreground-600">
                    {item.desc}
                  </p>
                  <p className="mt-3 border-t border-background-200 pt-3 text-xs text-foreground-500">
                    <i className="ri-time-line mr-1"></i>
                    {item.episodes}
                  </p>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}